import { ref } from 'vue'

import type {
  ComponentTypes,
  MultimediaProps,
  ComponentNode,
  ContentGridProps,
  LinkProps,
} from '@canvas-builder/types/canvas'
import type { Selection } from '@lib/types/selections'
import { FileContentTypeEnum, type CanvasRetrieve, type File, type FileRetrieve } from '@openapi'
import {
  isAsset,
  isExternalLink,
  type SelectedPage,
  type SelectedFile,
  type SelectorType,
  type SelectedExternalLink,
  type SelectedAsset,
} from '@content-selector/stores/app'
import { SUPPORTED_IMAGE_TYPES } from '@lib/constants/uploads.const'

type ContentSelectorPayload = {
  id: ComponentNode['id'] | null
  componentType: Extract<
    ComponentTypes,
    ComponentTypes.ContentGrid | ComponentTypes.Multimedia | ComponentTypes.Link
  > | null
  selections?: Selection[]
  allowedSelectorTypes?: SelectorType[]
  allowedContentTypes?: FileContentTypeEnum[]
  allowedAssetContentTypes?: FileContentTypeEnum[]
  allowedIds?: File['id'][]
  initialMetadataFilters?: Record<string, any>
  maxSelections?: number
  onlyAllowContentPdfPages?: boolean
  onSelected?: (selections: (SelectedFile | SelectedPage)[]) => void
  usedInSectionId?: CanvasRetrieve['id']
}

type ContentSelectorState = false | ContentSelectorPayload

const contentSelectorMode = ref<ContentSelectorState>(false)

function clearContentSelectorMode() {
  contentSelectorMode.value = false
}

function setContentSelectorMode(payload: ContentSelectorPayload) {
  const {
    id,
    componentType,
    selections,
    allowedSelectorTypes,
    allowedContentTypes,
    allowedAssetContentTypes,
    allowedIds,
    initialMetadataFilters,
    maxSelections,
    onlyAllowContentPdfPages,
    onSelected,
    usedInSectionId,
  } = payload
  const value: ContentSelectorState = { id, componentType, onSelected, usedInSectionId }
  allowedSelectorTypes && (value.allowedSelectorTypes = allowedSelectorTypes)
  allowedContentTypes && (value.allowedContentTypes = allowedContentTypes)
  allowedAssetContentTypes && (value.allowedAssetContentTypes = allowedAssetContentTypes)
  selections && (value.selections = selections)
  allowedIds && (value.allowedIds = allowedIds)
  initialMetadataFilters && (value.initialMetadataFilters = initialMetadataFilters)
  maxSelections && (value.maxSelections = maxSelections)
  onlyAllowContentPdfPages && (value.onlyAllowContentPdfPages = onlyAllowContentPdfPages)
  contentSelectorMode.value = value
}

export function prepareContentGridNodeData({
  selectedItems,
  filesById,
  node,
}: {
  selectedItems: (SelectedFile | SelectedPage)[]
  filesById: Record<FileRetrieve['id'], FileRetrieve>
  node?: ComponentNode<ContentGridProps>
}): ContentGridProps {
  const items = selectedItems.map((s): ContentGridProps['items'][number] => {
    if (s.type === 'file') {
      return {
        file: {
          id: s.id,
          name: s.name,
          thumbnail_url: s.thumbnail_url,
          content_thumbnails: filesById[s.id].content_thumbnails,
          content_url: filesById[s.id].content_url,
          content_type: filesById[s.id].content_type,
          content_extension: filesById[s.id].content_extension,
          content_length: filesById[s.id].content_length,
          permissions: filesById[s.id].permissions,
          expires_at: filesById[s.id].expires_at,
          tags: filesById[s.id].tags,
        },
        type: s.type,
      }
    }

    return {
      file: {
        id: s.file.id,
        name: s.file.name,
        permissions: s?.permissions,
        tags: filesById[s.file.id]?.tags,
      },
      slide: {
        index: s.page_index,
        url: s.thumbnail_url,
      },
      type: 'slide',
    }
  })

  return { ...node?.data, items }
}

function prepareLinkNodeData({
  selectedItems,
  filesById,
  node,
}: {
  selectedItems: (SelectedFile | SelectedPage)[]
  filesById: Record<FileRetrieve['id'], FileRetrieve>
  node?: ComponentNode<LinkProps>
}): LinkProps {
  const selectedItem = selectedItems[0]

  let item: Partial<File> & { page_index?: number }

  if (selectedItem.type === 'file') {
    item = {
      id: selectedItem.id,
      name: selectedItem.name,
      thumbnail_url: selectedItem.thumbnail_url,
      content_type: filesById[selectedItem.id].content_type,
    }
  } else {
    item = {
      id: selectedItem.file.id,
      name: selectedItem.file.name ?? '',
      page_index: selectedItem.page_index,
      thumbnail_url: selectedItem.thumbnail_url ?? '',
      content_type: FileContentTypeEnum.PDF,
    }
  }
  return { ...node?.data, file: item }
}

function prepareMultimediaNodeData({
  selectedItems,
}: {
  selectedItems: (SelectedExternalLink | SelectedAsset)[]
}): { data: MultimediaProps; theme_meta?: Partial<ComponentNode['theme_meta']> } | undefined {
  if (!selectedItems?.length) {
    return
  }

  const [item] = selectedItems

  const result: { data: MultimediaProps; theme_meta?: Partial<ComponentNode['theme_meta']> } = {
    data: {} as MultimediaProps,
  }

  if (isAsset(item)) {
    result.data = {
      asset_type: 'pitcher_asset',
      file: {
        id: item.id,
        name: item.file?.name || '',
        content_url: item.content_url,
      },
      type: SUPPORTED_IMAGE_TYPES.includes(item.content_extension ?? '') ? 'image' : 'video',
    }

    if (item.theme_meta) {
      result.theme_meta = item.theme_meta as Partial<ComponentNode['theme_meta']>
    }
  } else if (isExternalLink(item)) {
    result.data = {
      asset_type: 'external',
      external_url: item.url,
      type: item.content_type,
      from: item.from,
    }

    if ((item as any).theme_meta) {
      result.theme_meta = (item as any).theme_meta as Partial<ComponentNode['theme_meta']>
    }
  } else {
    console.warn('Unexpected item type', item)
    return
  }

  return result
}

export default function () {
  return {
    clearContentSelectorMode,
    contentSelectorMode,
    setContentSelectorMode,
    prepareContentGridNodeData,
    prepareLinkNodeData,
    prepareMultimediaNodeData,
  }
}
